import { useState, useEffect } from 'react';
import {
  DevisProjet,
  AssignationDevis,
  DemandeDevisRequest,
  BatimentConfigurationPublic,
  ApiResponse
} from '../types/api';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../utils/errorHandler';

export const useDevisRequests = () => {
  const [devisProjets, setDevisProjets] = useState<DevisProjet[]>([]);
  const [assignations, setAssignations] = useState<AssignationDevis[]>([]);
  const [availableTypes, setAvailableTypes] = useState<BatimentConfigurationPublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDevisProjets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDevisProjets();
      setDevisProjets(response.results);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors du chargement des devis'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAssignationsDevis();
      setAssignations(response.results);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors du chargement des assignations'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMesDemandes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMesDemandesDevis();
      setAssignations(response);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors du chargement de vos demandes'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTypesMaisonsDisponibles();
      setAvailableTypes(response);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors du chargement des types disponibles'));
    } finally {
      setLoading(false);
    }
  };

  const faireDemandeDevis = async (data: DemandeDevisRequest) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.faireDemandeDevis(data);
      // Rafraîchir les assignations après une nouvelle demande
      await fetchMesDemandes();
      return response;
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors de l\'envoi de la demande'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const accepterAssignation = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.accepterAssignationDevis(id);
      // Rafraîchir les assignations
      await fetchMesDemandes();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors de l\'acceptation de l\'assignation'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refuserAssignation = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.refuserAssignationDevis(id);
      // Rafraîchir les assignations
      await fetchMesDemandes();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Erreur lors du refus de l\'assignation'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevisProjets();
    fetchAssignations();
    fetchAvailableTypes();
  }, []);

  return {
    devisProjets,
    assignations,
    availableTypes,
    loading,
    error,
    fetchDevisProjets,
    fetchAssignations,
    fetchMesDemandes,
    fetchAvailableTypes,
    faireDemandeDevis,
    accepterAssignation,
    refuserAssignation,
  };
};
